'use client';

import React, { useState, useEffect } from 'react';
import { useTranslations, useLocale } from 'next-intl';
import { useRouter } from '@/i18n/navigation';
import { CreditCardIcon, PackageCheckIcon, ShoppingBagIcon, SearchIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Combobox } from '@/components/ui/combobox';
import { PhoneInput } from '@/components/ui/phone-input';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { guestCheckout } from '@/lib/api';
import { getErrorMessage } from '@/lib/utils';
import { useLocalCartStore, useMetadataStore } from '@/store';
import { useAppLocale, useCities, useCountries, useStates, useTranslatedRoute } from '@/hooks';
type PropsType = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};
type FormState = {
  firstname: string;
  lastname: string;
  company: string;
  email: string;
  phone: string;
  country: string;
  state: string;
  city: string;
  zipCode: string;
  addressText: string;
  notes: string;
};
const EMPTY_FORM: FormState = {
  firstname: '',
  lastname: '',
  company: '',
  email: '',
  phone: '',
  country: '',
  state: '',
  city: '',
  zipCode: '',
  addressText: '',
  notes: ''
};
const GuestCheckoutDialog = ({
  open,
  onOpenChange
}: PropsType) => {
  const t = useTranslations();
  const locale = useLocale();
  const router = useRouter();
  const {
    buildRoute
  } = useTranslatedRoute();
  const {
    backendLocale
  } = useAppLocale();
  const {
    items,
    clearItems
  } = useLocalCartStore();
  const metadata = useMetadataStore(state => state.metadata);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [placing, setPlacing] = useState(false);
  const [orderPlaced, setOrderPlaced] = useState(false);
  const [placedOrderId, setPlacedOrderId] = useState<string | null>(null);
  const [errors, setErrors] = useState<Partial<FormState>>({});
  const [contractAccepted, setContractAccepted] = useState(false);
  const [contractOpen, setContractOpen] = useState(false);
  const [countryId, setCountryId] = useState<string | undefined>(undefined);
  const [stateId, setStateId] = useState<string | undefined>(undefined);
  const [cityId, setCityId] = useState<string | undefined>(undefined);
  const {
    data: countries,
    isLoading: loadingCountries
  } = useCountries();
  const {
    data: states,
    isLoading: loadingStates
  } = useStates(countryId);
  const {
    data: cities,
    isLoading: loadingCities
  } = useCities(stateId);
  const hasCities = !!stateId && !loadingCities && (cities ?? []).length > 0;
  const distanceSellingContent = metadata?.contentSettings?.distanceSellingContract?.[backendLocale] ?? '';
  const canPlaceOrder = contractAccepted && !placing && items.length > 0;
  useEffect(() => {
    if (!open) {
      const timer = setTimeout(() => {
        setOrderPlaced(false);
        setForm(EMPTY_FORM);
        setErrors({});
        setPlacedOrderId(null);
        setContractAccepted(false);
        setContractOpen(false);
        setCountryId(undefined);
        setStateId(undefined);
        setCityId(undefined);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [open]);
  const set = (field: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm(prev => ({
      ...prev,
      [field]: e.target.value
    }));
    setErrors(prev_0 => ({
      ...prev_0,
      [field]: undefined
    }));
  };
  const validate = (): boolean => {
    const errs: Partial<FormState> = {};
    const shouldRequireCity = !!stateId && (cities ?? []).length > 0;
    if (!form.firstname.trim()) {
      errs.firstname = t('required');
    }
    if (!form.lastname.trim()) {
      errs.lastname = t('required');
    }
    if (!form.email.trim()) {
      errs.email = t('required');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      errs.email = t('invalid_email');
    }
    if (!form.phone.trim()) {
      errs.phone = t('required');
    }
    if (!form.country.trim()) {
      errs.country = t('required');
    }
    if (!form.state.trim()) {
      errs.state = t('required');
    }
    if (shouldRequireCity && !cityId) {
      errs.city = t('required');
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };
  const handlePlaceOrder = async () => {
    if (!validate()) {
      requestAnimationFrame(() => {
        const viewport = document.querySelector<HTMLElement>('#guest-checkout-scroll [data-slot="scroll-area-viewport"]');
        const firstErrorField = document.querySelector<HTMLElement>('#guest-checkout-scroll [data-has-error="true"]');
        if (viewport && firstErrorField) {
          viewport.scrollTop = firstErrorField.offsetTop - 16;
        }
        const firstInput = firstErrorField?.querySelector<HTMLElement>('input, button[role="combobox"]');
        firstInput?.focus();
      });
      return;
    }
    setPlacing(true);
    try {
      const order = await guestCheckout({
        firstname: form.firstname.trim(),
        lastname: form.lastname.trim(),
        company: form.company.trim() || undefined,
        email: form.email.trim(),
        phone: form.phone.trim() || undefined,
        items: items.map(i => ({
          productId: i.productId,
          quantity: i.quantity
        })),
        address: {
          country: form.country.trim(),
          state: form.state.trim() || undefined,
          city: form.city.trim() || undefined,
          zipCode: form.zipCode.trim() || undefined,
          addressText: form.addressText.trim() || undefined
        },
        notes: form.notes.trim() || undefined,
        locale
      });
      clearItems();
      setPlacedOrderId(order.id);
      setOrderPlaced(true);
    } catch (error: any) {
      toast.error(getErrorMessage(error, t('something_went_wrong')));
    } finally {
      setPlacing(false);
    }
  };
  const handleClose = () => onOpenChange(false);
  return <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent aria-describedby={undefined} className="sm:max-w-lg">
        {orderPlaced ? <div className="flex flex-1 flex-col items-center justify-center gap-6 px-8 py-12 text-center">
            <div className="flex h-20 w-20 items-center justify-center rounded-full bg-green-100">
              <PackageCheckIcon className="h-10 w-10 text-green-600" />
            </div>
            <div className="flex flex-col gap-2">
              <h2 className="text-xl font-bold text-gray-900">{t('order_placed_success_title')}</h2>
              <p className="text-muted-foreground text-sm">{t('guest_order_placed_success_desc')}</p>
              {placedOrderId && <p className="mt-1 rounded bg-gray-100 px-3 py-2 font-mono text-xs break-all text-gray-700">
                  {t('tracking_id')}: {placedOrderId}
                </p>}
            </div>
            <div className="flex w-full flex-col gap-3 pt-2">
              {placedOrderId && <Button className="w-full gap-2" onClick={() => {
            handleClose();
            router.push(`/order-track/${placedOrderId}`);
          }}>
                  <SearchIcon className="h-4 w-4" />
                  {t('track_order')}
                </Button>}
              <Button variant="outline" className="w-full gap-2" onClick={() => {
            handleClose();
            router.push(buildRoute('products'));
          }}>
                <ShoppingBagIcon className="h-4 w-4" />
                {t('continue_shopping')}
              </Button>
            </div>
          </div> : <>
            <DialogHeader className="px-6 pt-6 pb-4">
              <DialogTitle className="flex items-center gap-2">
                <CreditCardIcon className="text-muted-foreground h-5 w-5" />
                {t('guest_checkout')}
              </DialogTitle>
            </DialogHeader>

            <ScrollArea id="guest-checkout-scroll" className="min-h-0 flex-1" viewportClassName="max-h-[calc(90vh-9rem)] px-6 pb-4">
              <div className="flex flex-col gap-5">
                {/* Personal info */}
                <div className="flex flex-col gap-3">
                  <h3 className="text-sm font-semibold text-gray-900">{t('personal_information')}</h3>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="flex flex-col gap-1" data-has-error={!!errors.firstname || undefined}>
                      <label htmlFor="guest-checkout-firstname" className="text-xs font-medium text-gray-700">
                        {t('firstname')} <span className="text-destructive">*</span>
                      </label>
                      <Input id="guest-checkout-firstname" value={form.firstname} onChange={set('firstname')} placeholder={t('firstname')} className="h-9 text-sm" aria-invalid={!!errors.firstname || undefined} />
                      {errors.firstname && <span className="text-destructive text-xs">{errors.firstname}</span>}
                    </div>
                    <div className="flex flex-col gap-1" data-has-error={!!errors.lastname || undefined}>
                      <label htmlFor="guest-checkout-lastname" className="text-xs font-medium text-gray-700">
                        {t('lastname')} <span className="text-destructive">*</span>
                      </label>
                      <Input id="guest-checkout-lastname" value={form.lastname} onChange={set('lastname')} placeholder={t('lastname')} className="h-9 text-sm" aria-invalid={!!errors.lastname || undefined} />
                      {errors.lastname && <span className="text-destructive text-xs">{errors.lastname}</span>}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1" data-has-error={!!errors.email || undefined}>
                    <label htmlFor="guest-checkout-email" className="text-xs font-medium text-gray-700">
                      {t('email')} <span className="text-destructive">*</span>
                    </label>
                    <Input id="guest-checkout-email" type="email" value={form.email} onChange={set('email')} placeholder={t('your_email')} className="h-9 text-sm" aria-invalid={!!errors.email || undefined} />
                    {errors.email && <span className="text-destructive text-xs">{errors.email}</span>}
                  </div>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div className="flex flex-col gap-1">
                      <label htmlFor="guest-checkout-company" className="text-xs font-medium text-gray-700">
                        {t('company')}
                      </label>
                      <Input id="guest-checkout-company" value={form.company} onChange={set('company')} placeholder={t('company')} className="h-9 text-sm" />
                    </div>
                    <div className="flex flex-col gap-1" data-has-error={!!errors.phone || undefined}>
                      <label htmlFor="guest-checkout-phone" className="text-xs font-medium text-gray-700">
                        {t('phone')} <span className="text-destructive">*</span>
                      </label>
                      <PhoneInput id="guest-checkout-phone" international value={form.phone} onChange={value => {
                    setForm(prev_1 => ({
                      ...prev_1,
                      phone: value as string ?? ''
                    }));
                    setErrors(prev_2 => ({
                      ...prev_2,
                      phone: undefined
                    }));
                  }} aria-invalid={!!errors.phone || undefined} />
                      {errors.phone && <span className="text-destructive text-xs">{errors.phone}</span>}
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Shipping address */}
                <div className="flex flex-col gap-3">
                  <h3 className="text-sm font-semibold text-gray-900">{t('shipping_address')}</h3>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <div className="flex flex-col gap-1" data-has-error={!!errors.country || undefined}>
                      <label htmlFor="guest-checkout-country" className="text-xs font-medium text-gray-700">
                        {t('country')} <span className="text-destructive">*</span>
                      </label>
                      <Combobox id="guest-checkout-country" options={(countries ?? []).map(country => ({
                    label: `${country.emoji} ${country.name}`,
                    value: country.id,
                    searchValue: country.name
                  }))} value={countryId ?? ''} onValueChange={val => {
                    const nextValue = Array.isArray(val) ? val[0] : val;
                    const selected = (countries ?? []).find(country_0 => country_0.id === nextValue);
                    setCountryId(nextValue || undefined);
                    setStateId(undefined);
                    setCityId(undefined);
                    setForm(prev_3 => ({
                      ...prev_3,
                      country: selected?.name ?? '',
                      state: '',
                      city: ''
                    }));
                    setErrors(prev_4 => ({
                      ...prev_4,
                      country: undefined,
                      state: undefined,
                      city: undefined
                    }));
                  }} placeholder={t('select')} disabled={loadingCountries} aria-invalid={!!errors.country || undefined} clearable />
                      {errors.country && <span className="text-destructive text-xs">{errors.country}</span>}
                    </div>
                    <div className="flex flex-col gap-1" data-has-error={!!errors.state || undefined}>
                      <label htmlFor="guest-checkout-state" className="text-xs font-medium text-gray-700">
                        {t('state')} <span className="text-destructive">*</span>
                      </label>
                      <Combobox id="guest-checkout-state" options={(states ?? []).map(state_0 => ({
                    label: state_0.name,
                    value: state_0.id,
                    searchValue: state_0.name
                  }))} value={stateId ?? ''} onValueChange={val_0 => {
                    const nextValue_0 = Array.isArray(val_0) ? val_0[0] : val_0;
                    const selected_0 = (states ?? []).find(state_1 => state_1.id === nextValue_0);
                    setStateId(nextValue_0 || undefined);
                    setCityId(undefined);
                    setForm(prev_5 => ({
                      ...prev_5,
                      state: selected_0?.name ?? '',
                      city: ''
                    }));
                    setErrors(prev_6 => ({
                      ...prev_6,
                      state: undefined,
                      city: undefined
                    }));
                  }} placeholder={t('select')} disabled={!countryId || loadingStates} aria-invalid={!!errors.state || undefined} clearable />
                      {errors.state && <span className="text-destructive text-xs">{errors.state}</span>}
                    </div>
                    <div className="flex flex-col gap-1" data-has-error={!!errors.city || undefined}>
                      <label htmlFor="guest-checkout-city" className="text-xs font-medium text-gray-700">
                        {t('city')} {hasCities && <span className="text-destructive">*</span>}
                      </label>
                      <Combobox id="guest-checkout-city" options={(cities ?? []).map(city => ({
                    label: city.name,
                    value: city.id,
                    searchValue: city.name
                  }))} value={cityId ?? ''} onValueChange={val_1 => {
                    const nextValue_1 = Array.isArray(val_1) ? val_1[0] : val_1;
                    const selected_1 = (cities ?? []).find(city_0 => city_0.id === nextValue_1);
                    setCityId(nextValue_1 || undefined);
                    setForm(prev_7 => ({
                      ...prev_7,
                      city: selected_1?.name ?? ''
                    }));
                    setErrors(prev_8 => ({
                      ...prev_8,
                      city: undefined
                    }));
                  }} placeholder={t('select')} disabled={!stateId || loadingCities} aria-invalid={!!errors.city || undefined} clearable />
                      {errors.city && <span className="text-destructive text-xs">{errors.city}</span>}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1">
                    <label htmlFor="guest-checkout-zip-code" className="text-xs font-medium text-gray-700">
                      {t('zip_code')}
                    </label>
                    <Input id="guest-checkout-zip-code" value={form.zipCode} onChange={set('zipCode')} placeholder={t('address_zip_placeholder')} className="h-9 text-sm" />
                  </div>
                  <div className="flex flex-col gap-1">
                    <label htmlFor="guest-checkout-address-detail" className="text-xs font-medium text-gray-700">
                      {t('address_detail')}
                    </label>
                    <Textarea id="guest-checkout-address-detail" value={form.addressText} onChange={set('addressText')} placeholder={t('address_text_placeholder')} className="resize-none text-sm" rows={2} />
                  </div>
                </div>

                <Separator />

                {/* Notes */}
                <div className="flex flex-col gap-2">
                  <label htmlFor="guest-checkout-notes" className="text-sm font-semibold text-gray-900">
                    {t('order_notes')}
                    <span className="text-muted-foreground ml-1 font-normal">({t('optional')})</span>
                  </label>
                  <Textarea id="guest-checkout-notes" value={form.notes} onChange={set('notes')} placeholder={t('order_notes_placeholder')} className="resize-none text-sm" rows={3} maxLength={500} />
                </div>

                <Separator />

                {/* Distance selling contract */}
                <div className="flex cursor-pointer items-start gap-2" onClick={() => setContractAccepted(prev_9 => !prev_9)}>
                  <Checkbox checked={contractAccepted} onCheckedChange={val_2 => setContractAccepted(!!val_2)} className="pointer-events-none mt-0.5" />
                  <span className="text-sm leading-snug text-gray-700 select-none">
                    {t.rich('i_have_read_and_accept_distance_selling', {
                  link: chunks => <button type="button" onClick={e_0 => {
                    e_0.stopPropagation();
                    setContractOpen(true);
                  }} className="text-brand font-medium hover:underline">
                          {chunks}
                        </button>
                })}
                  </span>
                </div>
              </div>
            </ScrollArea>

            <DialogFooter className="border-t px-6 py-4">
              <Button variant="outline" onClick={handleClose} disabled={placing}>
                {t('cancel')}
              </Button>
              <Button className="gap-2 bg-green-600 text-white hover:bg-green-700" disabled={!canPlaceOrder} onClick={handlePlaceOrder}>
                <CreditCardIcon className="h-4 w-4" />
                {placing ? t('placing_order') : t('place_order')}
              </Button>
            </DialogFooter>
          </>}
      </DialogContent>

      <Dialog open={contractOpen} onOpenChange={setContractOpen}>
        <DialogContent aria-describedby={undefined} className="max-h-[85vh] sm:max-w-2xl">
          <DialogHeader className="border-b px-6 py-4">
            <DialogTitle>{t('distance_selling_contract')}</DialogTitle>
          </DialogHeader>
          <ScrollArea className="min-h-0 flex-1" viewportClassName="max-h-[calc(85vh-5rem)] px-6 py-5">
            <div className="prose prose-sm max-w-none pb-1 text-sm leading-relaxed whitespace-pre-wrap">
              {distanceSellingContent || <span className="text-muted-foreground">{t('no_content')}</span>}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </Dialog>;
};
export default GuestCheckoutDialog;